import { LitElement, html, css } from 'lit'
import { api } from '../api.js'
import * as lookups from '../lookups.js'
import { toast } from './app-toast.js'

// Display names for the collectors an entry's supporting activity came from
// (keyed by the audit-finding provider). Unknown keys fall back to the raw key.
const SOURCE_LABEL = {
  github: 'GitHub',
  support: 'BrandExtract Support',
  zendesk: 'Zendesk',
  gmail: 'Google Email',
  gcalendar: 'Google Calendar',
  gchat: 'Google Chat',
  gdrive: 'Google Drive',
  zoom: 'Zoom'
}

// A task option, shown the way Workamajig's own schedule shows it: numbered and
// indented under its parent. A task's id IS its outline number (4, 4.1, 4.1.2),
// so the dots give the depth. A native <option> ignores padding and text-indent,
// so the indent has to live in the label itself, as non-breaking spaces.
function taskLabel(task) {
  const id = String(task.id ?? '')
  const depth = (id.match(/\./g) || []).length
  return `${'\u00A0'.repeat(depth * 4)}${id} - ${task.name}`
}

// A single editable time entry, restyled to the BrandExtract design system: a
// white card holding a five-column grid —
//   Client / Service | Project / Task | Hours | Description | Approve · Reject
// — over a tinted "Found in" band naming the collectors that surfaced it, which
// also carries the merge checkbox at its right edge. Each "Found in" chip opens
// the source material behind the entry (the ticket, the thread, the event, the
// file). The chip itself is the first link; when the collector contributed more,
// numbered links 2, 3, … follow it, one per further item, so every source can be
// reached in a click. Above that band sit Ziggy's own notes about the
// row: how the hours were arrived at ("Estimate"), then any advisory ("Heads
// up"), then config notes. None of those reach Workamajig.
//
// Approve/Reject only appear for rows Ziggy's audit found that are still pending
// (source='ziggy', status='draft'). Everything else shows a status pill saying what
// was decided and where the row came from — Approved for Ziggy's own entries the
// user accepted, Workamajig for time entered over there by hand (see _statusPill).
// Rows already in Workamajig are locked (handled server-side in entries.service).
//
// Owns its cascading select option lists and persists field edits via PATCH. Emits:
//   'entry-saved'  (detail: updatedEntry)             after a field PATCH
//   'entry-action' (detail: { action, id })           for approve / reject / unmerge
//   'entry-select' (detail: { id, selected })         when the merge checkbox is ticked
class EntryRow extends LitElement {
  static properties = {
    entry: { type: Object },
    // Ticked by the user to pick this row for a merge. Held by the day page, which
    // counts the picks and shows the Merge button once there are two.
    selected: { type: Boolean },
    _clients: { state: true },
    _projects: { state: true },
    _tasks: { state: true },
    _services: { state: true },
    // Set when this entry's job is pinned to a single service code in
    // config/settings.json — the Service select then shows that code, locked.
    _enforcedService: { state: true },
    // config/settings.json's restrictedServices: services that may only be
    // booked to certain jobs, dropped from the select on every other job.
    _restrictions: { state: true },
    _busy: { state: true },
    _acting: { state: true },
    // True until the client/project/service/task option lists have loaded, so a
    // freshly-audited row shows a throbber until its matched values can be shown.
    // Set synchronously at the start of _loadOptions (called from
    // connectedCallback), so the first render already shows the throbber.
    _loadingOptions: { state: true },
    // Set when the option lists couldn't be loaded. An empty dropdown and a
    // failed fetch look identical to a user, and silently rendering the former
    // for the latter is how "I can't see any clients" went unreported.
    _optionsError: { state: true }
  }

  static styles = css`
    /* Shadow DOM does not inherit the document's box-sizing reset, so set it here
       — otherwise width:100% controls overflow their padded cells. */
    *, *::before, *::after { box-sizing: border-box; }

    :host { display: block; position: relative; }

    .card { background: var(--white); border: 1px solid var(--neutral-100); border-radius: 10px;
            box-shadow: var(--shadow-cell); overflow: hidden; margin-top: 12px;
            transition: box-shadow .16s ease; }
    .card:hover { box-shadow: var(--shadow-row); }
    .card.rejected { opacity: 0.62; }
    /* Picked out for a merge. */
    .card.picked { box-shadow: 0 0 0 2px var(--eggplant-500); border-color: var(--eggplant-500); }

    .body { display: grid; grid-template-columns: 1.15fr 1.5fr 62px 1.7fr 118px;
            gap: 12px; align-items: start; padding: 14px 16px; }
    .stack { display: flex; flex-direction: column; gap: 7px; min-width: 0; }

    /* Native selects styled as the design's squared-off pickers — appearance:none
       plus a drawn chevron, so the control stays a real <select> (keyboard,
       mobile pickers, assistive tech) while matching the mock. */
    select, input, textarea {
      width: 100%; font-family: var(--font-sans); font-size: 12.5px; line-height: 1.4;
      background: var(--white); border: 1px solid var(--border-input); border-radius: 5px;
      color: var(--neutral-700); padding: 6px 9px; min-height: 30px;
      transition: border-color .15s ease;
    }
    select { appearance: none; -webkit-appearance: none; padding-right: 26px; cursor: pointer;
             text-overflow: ellipsis; white-space: nowrap; overflow: hidden;
             background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%23808080' stroke-width='1.5'/%3E%3C/svg%3E");
             background-repeat: no-repeat; background-position: right 9px center; background-size: 10px 7px; }
    select:hover:not(:disabled), input:hover:not(:disabled), textarea:hover:not(:disabled) {
      border-color: var(--eggplant-300); }
    select:focus, input:focus, textarea:focus { outline: none; border-color: var(--eggplant-500); }
    /* background-COLOR, not the shorthand — the shorthand would clear the
       chevron the select rule above paints, leaving read-only rows looking
       like plain text fields. */
    select:disabled, input:disabled, textarea:disabled {
      background-color: var(--neutral-50); color: var(--neutral-500); cursor: default; }

    input.hours { color: var(--eggplant-500); font-weight: 700; font-variant-numeric: tabular-nums; }
    /* Drop the number spinner so the value uses the full narrow cell width. */
    input.hours { -moz-appearance: textfield; }
    input.hours::-webkit-outer-spin-button,
    input.hours::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    textarea.desc { resize: vertical; min-height: 62px; }
    textarea.desc::placeholder, input::placeholder { color: var(--neutral-500); }

    /* Empty required field on an editable draft — needs a value before approving. */
    select.invalid, input.invalid { border-color: #B4232B; box-shadow: 0 0 0 1px rgba(180, 35, 43, 0.4); }

    .actions { display: flex; flex-direction: column; align-items: stretch; gap: 7px; }
    .actions button { font-family: var(--font-sans); font-size: 12px; cursor: pointer;
                      border-radius: 5px; padding: 7px 10px; width: 100%; }
    button.approve { font-weight: 700; color: var(--white); background: var(--eggplant-600);
                     border: 1px solid var(--eggplant-600); }
    button.approve:hover:not(:disabled) { background: var(--eggplant-700); border-color: var(--eggplant-700); }
    button.reject { font-weight: 600; color: var(--neutral-700); background: var(--white);
                    border: 1px solid var(--border-input); }
    button.reject:hover:not(:disabled) { border-color: var(--eggplant-300); }
    .actions button:disabled { opacity: 0.5; cursor: default; }

    /* Status pill for rows the user can no longer act on. */
    .pill { align-self: flex-end; font-family: var(--font-eyebrow); font-size: 10px;
            font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
            border-radius: 999px; padding: 4px 10px; white-space: nowrap; }
    .pill.workamajig { color: var(--sky-700); background: var(--sky-50); border: 1px solid var(--sky-200); }
    .pill.approved { color: var(--chartreuse-800); background: var(--chartreuse-100);
                     border: 1px solid var(--chartreuse-300); }
    .pill.rejected, .pill.draft { color: var(--neutral-700); background: var(--neutral-100);
                                  border: 1px solid var(--neutral-200); }

    /* Footer bands: "Found in" attribution, plus anything Ziggy wants to say
       about the row (an advisory, or a failed lookup). */
    .band { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 9px 16px;
            border-top: 1px solid var(--neutral-100); background: var(--neutral-50); }
    .band-label { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700;
                  letter-spacing: 0.12em; text-transform: uppercase; color: var(--neutral-500); }
    .chip { font-size: 11.5px; font-weight: 600; color: var(--eggplant-500); background: var(--white);
            border: 1px solid var(--neutral-100); border-radius: 999px; padding: 3px 10px;
            display: inline-flex; align-items: center; gap: 5px; line-height: 1.3; }
    /* A chip that goes somewhere: the source material behind the entry. The arrow
       is the only thing that separates it from the inert kind, so it is always on. */
    .chip.link { text-decoration: none; cursor: pointer; }
    .chip.link:hover { border-color: var(--eggplant-300); color: var(--eggplant-600);
                       box-shadow: var(--shadow-cell); }
    .chip.link:focus-visible { outline: 2px solid var(--eggplant-500); outline-offset: 1px; }
    .chip .arrow { font-size: 10px; opacity: 0.7; }
    /* A source with several items behind it: the chip opens the first, and these
       numbered links — 2, 3, … — open each further one. Kept flush against the
       chip as one group, so the numbers read as "more of the same source". */
    .chip-group { display: inline-flex; align-items: center; gap: 3px; }
    .chip-group .more { display: inline-flex; align-items: center; justify-content: center;
                        min-width: 22px; height: 22px; padding: 0 6px; border-radius: 999px;
                        font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums;
                        color: var(--eggplant-500); background: var(--white);
                        border: 1px solid var(--neutral-100); text-decoration: none; }
    .chip-group .more:hover { border-color: var(--eggplant-300); color: var(--eggplant-600);
                              box-shadow: var(--shadow-cell); }
    .chip-group .more:focus-visible { outline: 2px solid var(--eggplant-500); outline-offset: 1px; }
    .band .grow { flex: 1 1 auto; }

    /* Merge pick, parked at the right edge of the "Found in" band. Always on show
       for a mergeable row — no mode to enter first — and unticked until the user
       ticks it. The whole label is the hit target, so the 17px box isn't the only
       thing to aim at. */
    label.pick { display: flex; align-items: center; gap: 7px; cursor: pointer;
                 font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700;
                 letter-spacing: 0.12em; text-transform: uppercase; color: var(--neutral-500);
                 white-space: nowrap; user-select: none; }
    label.pick:hover { color: var(--eggplant-600); }
    /* Undo the shared select/input styling above — a checkbox is not a field, and
       the border/background it sets would fight the native control. */
    input.pickbox { appearance: auto; -webkit-appearance: checkbox;
                    width: 17px; height: 17px; min-height: 0; padding: 0; margin: 0;
                    border: initial; background: initial;
                    accent-color: var(--eggplant-600); cursor: pointer; }
    /* How the hours were arrived at, from the collector ("estimated from the
       replies logged in Support"). Ziggy-only, like the notice below it — the
       description is what goes to Workamajig, and from there onto client billing,
       so this must never be written there. Quieter than the notice: it explains
       a number rather than flagging a problem. */
    .band.estimate { background: var(--sky-50); color: var(--sky-700);
                     font-size: 12.5px; font-weight: 500; line-height: 1.45; }
    /* Ziggy advisory (e.g. possible under-logged time) shown beneath an entry.
       Also carries the config notes from config/notes.json, which are the same
       kind of thing to read — a "warning" one takes the error palette. */
    .band.notice { background: var(--chartreuse-bg); color: var(--chartreuse-800);
                   font-size: 12.5px; font-weight: 600; line-height: 1.45; }
    .band.notice.warning { background: #FDF4F4; color: #B4232B; }
    /* Merge history: what this row was made of, and the way back. */
    .band.merged { background: var(--eggplant-50); color: var(--eggplant-700);
                   font-size: 12.5px; line-height: 1.45; }
    .band.merged button { font: inherit; font-weight: 700; padding: 4px 12px; border-radius: 5px;
                          border: 1px solid var(--eggplant-500); background: var(--white);
                          color: var(--eggplant-600); cursor: pointer; white-space: nowrap; }
    .band.merged button:hover:not(:disabled) { background: var(--eggplant-500); color: var(--white); }
    .band.merged button:disabled { opacity: 0.5; cursor: default; }
    /* Shown when the cascading selects couldn't be filled — see _optionsError. */
    .band.error { background: #FDF4F4; color: #B4232B; font-size: 12.5px; font-weight: 600; }
    .band.error button { font: inherit; font-weight: 700; padding: 3px 10px; border-radius: 999px;
                         border: 1px solid currentColor; background: none; color: inherit; cursor: pointer; }

    @keyframes zg-spin { to { transform: rotate(360deg); } }
    .spinner { display: inline-block; width: 0.8em; height: 0.8em; border: 2px solid currentColor;
               border-right-color: transparent; border-radius: 50%; animation: zg-spin 0.6s linear infinite;
               vertical-align: -0.1em; }
    /* Covers the row while its client/project/service/task lists are matching. */
    .matching { position: absolute; inset: 12px 0 0; z-index: 2; display: flex; align-items: center;
                justify-content: center; gap: 8px; border-radius: 10px;
                background: rgba(255, 255, 255, 0.78); color: var(--neutral-600); font-size: 12.5px; }

    /* Mobile: stack every field into one column in a fixed order. The two action
       buttons share the final row, side by side. */
    @media (max-width: 640px) {
      .body { grid-template-columns: 1fr; gap: 9px; }
      .actions { flex-direction: row; }
      .pill { align-self: flex-start; }
    }
  `

  connectedCallback() {
    super.connectedCallback()
    this._loadOptions()
  }

  async _loadOptions() {
    this._loadingOptions = true
    this._optionsError = null
    // Resolved on their own, ahead of the lookups: which services this job may
    // book to must not depend on a Workamajig call succeeding, or a failed
    // clients/services fetch would fall through to the free-choice dropdown.
    this._enforcedService = await lookups.enforcedService(this.entry.projectId)
    this._restrictions = await lookups.serviceRestrictions()
    try {
      // Services are fetched FOR THIS JOB, so the server has already dropped the
      // ones it may not book to; the client-side filter below is a second pass
      // for a list that arrived before the job was chosen.
      const [c, s] = await Promise.all([lookups.clients(), lookups.services(this.entry.projectId)])
      this._clients = c
      this._services = s
      // Load the dependent lists so the audited client → project → task match
      // is resolvable; keep the throbber up until all of them are in.
      if (this.entry.clientId) this._projects = await lookups.projects(this.entry.clientId)
      if (this.entry.projectId) this._tasks = await lookups.tasks(this.entry.projectId)
    } catch (err) {
      // Still reveal the row so the user can match manually — but SAY SO. An
      // empty select is indistinguishable from "Workamajig has nothing for you",
      // and lookups.js no longer caches the failure, so retrying is worthwhile.
      this._optionsError = err?.message || 'Could not load Workamajig lists.'
      console.warn('Ziggy: could not load entry-row option lists', err)
    } finally {
      this._loadingOptions = false
    }
  }

  // Re-run the lookups after a failure. lookups.js caches only successful,
  // non-empty results, so this genuinely refetches rather than replaying the error.
  _retryOptions() {
    this._loadOptions()
  }

  get _readonly() {
    const e = this.entry
    // Rejected rows, and anything already posted to Workamajig (mirrored, or
    // approved and pushed), are read-only — Workamajig owns those.
    return e.status === 'rejected' || e.status === 'approved' || e.source === 'workamajig'
  }

  async _patch(fields) {
    this._busy = true
    try {
      const updated = await api.patch(`/entries/${this.entry.id}`, fields)
      this.entry = updated
      this.dispatchEvent(new CustomEvent('entry-saved', { detail: updated, bubbles: true, composed: true }))
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      this._busy = false
    }
  }

  async _onClient(e) {
    const id = e.target.value
    // Optional-chained: the list is absent when its load failed, and the row is
    // still interactive in that state.
    const name = this._clients?.find(c => c.id === id)?.name || null
    // Project is about to be cleared, so nothing is pinned or narrowed any more —
    // otherwise the select would stay locked to the previous job's code, or keep
    // offering only that job's services, with no job set.
    this._enforcedService = null
    this._tasks = []
    // A failed lookup here used to reject unhandled and abandon the PATCH, so the
    // select snapped back with no explanation. Report it and still save the
    // client the user picked — the dependent lists can be retried.
    try {
      this._projects = await lookups.projects(id)
      this._services = await lookups.services()
      this._optionsError = null
    } catch (err) {
      this._optionsError = err?.message || 'Could not load Workamajig lists.'
    }
    // Changing client clears project and task (sent as '' to clear server-side).
    await this._patch({ clientId: id, clientName: name, projectId: '', projectName: '', taskId: '', taskName: '' })
  }

  async _onProject(e) {
    const id = e.target.value
    const proj = this._projects?.find(p => p.id === id)
    // The new job has its own set of bookable services, and may pin one (the
    // PATCH response carries the code the server set) — reload both before the
    // row re-renders, so the Service select never shows the old job's options.
    try {
      const [tasks, services, enforced] = await Promise.all([
        lookups.tasks(id), lookups.services(id), lookups.enforcedService(id)
      ])
      this._tasks = tasks
      this._services = services
      this._enforcedService = enforced
      this._optionsError = null
    } catch (err) {
      this._optionsError = err?.message || 'Could not load Workamajig lists.'
    }
    await this._patch({ projectId: id, projectName: proj?.name || null, taskId: '', taskName: '' })
  }

  _onTask(e) {
    const id = e.target.value
    const name = this._taskOptions().find(t => t.id === id)?.name || null
    this._patch({ taskId: id, taskName: name })
  }

  _onService(e) {
    this._patch({ serviceCode: e.target.value })
  }

  // Display label for a service code. The global list may not carry a pinned
  // code (a user's service list can be narrower than config), so fall back to
  // showing the code itself.
  _serviceName(code) {
    return this._services?.find(s => s.code === code)?.name || code
  }

  // The services this entry's job may book to: everything unrestricted, plus the
  // restricted ones this job is listed for. Until a job is picked nothing can be
  // judged, so the whole list stands. The entry's own code always survives, so a
  // row that already holds a service (a Workamajig mirror, or an entry that
  // predates the restriction) still displays what it actually has.
  _allowedServices() {
    const services = this._services || []
    if (!this.entry.projectId || !this._restrictions?.length) return services
    return services.filter(s =>
      s.code === this.entry.serviceCode ||
      lookups.serviceAllowedOnJob(this._restrictions, s, this.entry.projectId))
  }

  // The project's schedule as the select shows it: every task, each flagged
  // `bookable` by the server. The un-bookable ones (phase headings, milestone
  // markers — anything without "Track Budgets" ticked in Workamajig) are kept in
  // the list but rendered disabled, so the outline still reads as an outline and
  // the numbered children aren't left under no visible parent.
  //
  // An entry can also carry a task that is no longer in the schedule at all — a
  // Workamajig mirror, or a row that predates an edit over there. Show that one
  // too, disabled, so the select says what the row actually holds instead of
  // snapping to "— Task —".
  _taskOptions() {
    const tasks = this._tasks || []
    const current = this.entry.taskId
    if (!current || tasks.some(t => t.id === current)) return tasks
    return [{ id: current, name: this.entry.taskName || '', bookable: false }, ...tasks]
  }

  _onHours(e) {
    const value = parseFloat(e.target.value)
    if (isNaN(value)) return
    this._patch({ hours: value })
  }

  _onDescription(e) {
    this._patch({ description: e.target.value })
  }

  // Reset in-progress state whenever the entry is (re)assigned — e.g. after the
  // parent reloads the day following an approve/reject. The row instance is
  // reused (keyed by id), so without this the spinner/disabled state would stick.
  willUpdate(changed) {
    if (changed.has('entry')) {
      this._busy = false
      this._acting = null
    }
  }

  _action(action) {
    // Show a spinner on the clicked button and lock the row until the parent
    // finishes the request and reloads (which clears this via willUpdate).
    this._busy = true
    this._acting = action
    this.dispatchEvent(new CustomEvent('entry-action', {
      detail: { action, id: this.entry.id }, bubbles: true, composed: true
    }))
  }

  // Only Ziggy's un-reviewed candidates can take part in a merge — the same rule
  // the server enforces (services/entries.service.js, assertMergeable).
  get _mergeable() {
    return this.entry.source === 'ziggy' && this.entry.status === 'draft'
  }

  _onPick(e) {
    this.dispatchEvent(new CustomEvent('entry-select', {
      detail: { id: this.entry.id, selected: e.target.checked }, bubbles: true, composed: true
    }))
  }

  // "Merged" band: what undoing the last merge on this row would give back. The
  // count is the rows that come back, not the activities behind them — the notice
  // band above already covers those.
  _mergedBand() {
    const { merges, restores } = this.entry.merge
    const rows = `${restores} ${restores === 1 ? 'entry' : 'entries'}`
    const stacked = merges > 1 ? ` (${merges} merges, undone one at a time)` : ''
    return html`
      <div class="band merged">
        <span class="band-label">Merged</span>
        <span class="grow">Unmerging puts ${rows} back${stacked}.</span>
        <button ?disabled=${this._busy} title="Undo the last merge on this entry"
                @click=${() => this._action('unmerge')}>
          ${this._acting === 'unmerge' ? html`<span class="spinner"></span> Unmerging…` : 'Unmerge'}
        </button>
      </div>`
  }

  // One "Found in" chip. The chip IS the link to the first item behind the
  // source; each further item gets a numbered link after it — 2, 3, … — so every
  // reference is one click away and none is hidden behind a menu. With no links
  // (a source whose collector predates them, or one with no web view) it is a
  // plain label. All open in a new tab — the person is mid-review and the row is
  // where they are.
  _sourceChip(provider) {
    const label = SOURCE_LABEL[provider] || provider
    const links = (this.entry.links || []).filter(l => l.provider === provider)
    if (!links.length) return html`<span class="chip">${label}</span>`
    const [first, ...rest] = links
    const chip = html`
      <a class="chip link" href=${first.url} target="_blank" rel="noopener noreferrer"
         title=${first.label}>${label} <span class="arrow">↗</span></a>`
    if (!rest.length) return chip
    return html`
      <span class="chip-group">
        ${chip}
        ${rest.map((l, i) => html`
          <a class="more" href=${l.url} target="_blank" rel="noopener noreferrer"
             title=${l.label} aria-label="${label} ${i + 2}: ${l.label}">${i + 2}</a>`)}
      </span>`
  }

  // The pill shown in place of the buttons: what was decided about the row, and
  // where it came from.
  //
  // "Approved" means Ziggy found it and the user accepted it; "Workamajig" means
  // somebody typed it into Workamajig themselves. That distinction rides on
  // `discoveredByZiggy`, NOT on `source`: source is rewritten to 'workamajig' the
  // first time an approved entry is mirrored back from Workamajig (the row becomes
  // its own mirror — see reconcileFromWorkamajig), which used to relabel the user's
  // own approvals as though they had been entered by hand over there.
  _statusPill() {
    const e = this.entry
    if (e.status === 'rejected') return { cls: 'rejected', text: 'Rejected' }
    if (e.status === 'draft') return { cls: 'draft', text: 'Draft' }
    if (e.discoveredByZiggy) return { cls: 'approved', text: 'Approved' }
    return { cls: 'workamajig', text: 'Workamajig' }
  }

  render() {
    const e = this.entry
    const ro = this._readonly || this._busy
    // Only audit-found, still-pending rows get Approve / Reject.
    const pending = e.source === 'ziggy' && e.status === 'draft'
    // Client, Project, Hours, Service, and Task are all required before an entry
    // can be approved (mirrors the server-side check). A pinned service counts as
    // present even if the row hasn't been written with it yet — approve applies it.
    const serviceCode = this._enforcedService || e.serviceCode
    const complete = !!(e.clientId && e.projectId && serviceCode && e.taskId &&
      e.hours != null && Number(e.hours) > 0)
    // Editable pending rows flag their empty required fields in red so the user
    // knows what to fill before they can approve.
    const need = pending && !ro
    const invalid = v => (need && !v ? 'invalid' : '')
    const pill = this._statusPill()

    return html`
      <div class="card ${e.status === 'rejected' ? 'rejected' : ''} ${this.selected ? 'picked' : ''}">
        <div class="body">
          <div class="stack">
            <select class=${invalid(e.clientId)} ?disabled=${ro} @change=${this._onClient}>
              <option value="">— Client —</option>
              ${(this._clients || []).map(c => html`
                <option value=${c.id} ?selected=${c.id === e.clientId}>${c.name}</option>`)}
            </select>
            ${this._enforcedService
              ? html`
                <select disabled
                        title="This job always books to ${this._enforcedService} (set in config/settings.json)">
                  <option selected>${this._serviceName(this._enforcedService)}</option>
                </select>`
              : html`
                <select class=${invalid(e.serviceCode)} ?disabled=${ro} @change=${this._onService}>
                  <option value="">— Service —</option>
                  ${this._allowedServices().map(s => html`
                    <option value=${s.code} ?selected=${s.code === e.serviceCode}>${s.name}</option>`)}
                </select>`}
          </div>

          <div class="stack">
            <select class=${invalid(e.projectId)} ?disabled=${ro || !e.clientId} @change=${this._onProject}>
              <option value="">— Project —</option>
              ${(this._projects || []).map(p => html`
                <option value=${p.id} ?selected=${p.id === e.projectId}>${p.id} - ${p.name}</option>`)}
            </select>
            <select class=${invalid(e.taskId)} ?disabled=${ro || !e.projectId} @change=${this._onTask}>
              <option value="">— Task —</option>
              ${this._taskOptions().map(t => html`
                <option value=${t.id} ?selected=${t.id === e.taskId}
                        ?disabled=${t.bookable === false}
                        title=${t.bookable === false ? 'Workamajig does not track budgets on this task, so it cannot take time' : ''}
                        >${taskLabel(t)}</option>`)}
            </select>
          </div>

          <div>
            <input class="hours ${need && !(Number(e.hours) > 0) ? 'invalid' : ''}" type="number"
                   min="0" step="0.25" placeholder="Hours" aria-label="Hours"
                   .value=${e.hours ?? ''} ?disabled=${ro} @change=${this._onHours} />
          </div>

          <div>
            <textarea class="desc" rows="3" ?disabled=${ro} placeholder="Description"
                      aria-label="Description"
                      @change=${this._onDescription}>${e.description ?? ''}</textarea>
          </div>

          <div class="actions">
            ${pending
              ? html`
                <button class="approve" ?disabled=${this._busy || !complete}
                  title=${complete ? 'Approve and save to Workamajig' : 'Client, Project, Hours, Service, and Task are all required'}
                  @click=${() => this._action('approve')}>
                  ${this._acting === 'approve' ? html`<span class="spinner"></span> Saving…` : 'Approve'}
                </button>
                <button class="reject" ?disabled=${this._busy} @click=${() => this._action('reject')}>
                  ${this._acting === 'reject' ? html`<span class="spinner"></span> …` : 'Reject'}
                </button>`
              : html`<span class="pill ${pill.cls}">${pill.text}</span>`}
          </div>
        </div>

        ${e.hoursNote ? html`
          <div class="band estimate">
            <span class="band-label">Estimate</span>${e.hoursNote}
          </div>` : ''}

        ${e.notice ? html`
          <div class="band notice">
            <span class="band-label">Heads up</span>${e.notice}
          </div>` : ''}

        ${(e.notes || []).map(n => html`
          <div class="band notice ${n.severity === 'warning' ? 'warning' : ''}">
            <span class="band-label">Note</span>${n.text}
          </div>`)}

        ${e.merge && this._mergeable ? this._mergedBand() : ''}

        ${!this._loadingOptions && this._optionsError ? html`
          <div class="band error" role="alert">
            <span>Couldn't load the Workamajig lists — ${this._optionsError}</span>
            <button @click=${this._retryOptions}>Retry</button>
          </div>` : ''}

        ${e.sources?.length || this._mergeable ? html`
          <div class="band">
            ${e.sources?.length ? html`
              <span class="band-label">Found in</span>
              ${e.sources.map(s => this._sourceChip(s))}` : ''}
            <span class="grow"></span>
            ${this._mergeable ? html`
              <label class="pick" title="Pick this entry, and at least one more, to merge them">
                Merge
                <input class="pickbox" type="checkbox" .checked=${!!this.selected}
                       aria-label="Select this entry to merge" @change=${this._onPick} />
              </label>` : ''}
          </div>` : ''}
      </div>

      ${this._loadingOptions ? html`
        <div class="matching" role="status" aria-live="polite">
          <span class="spinner"></span> Matching client, project, service &amp; task…
        </div>` : ''}
    `
  }
}

customElements.define('entry-row', EntryRow)
